import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, ResolveEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AnnualReportProvider, FeatureState } from '../../../../../aumico-lib-annualreport-fe/src/public-api';
import { ProjectsOverviewActions } from '../../../../../aumico-lib-projects-fe/src/lib';

@Component({
    selector: 'app-annualreport',
    template: `<router-outlet></router-outlet>`,
})
export class AnnualReportPage implements OnInit, OnDestroy {
    /** The project whose report the store was last loaded for; `null` while no project is open. */
    private loadedProjectId: string | null = null;
    private projectChanges: Subscription | undefined;

    constructor(
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private store: Store<FeatureState>,
        private annualReportProvider: AnnualReportProvider,
    ) {
        // This component serves as a container for the annual report microservice.
        // It allows for lazy loading of the annual report module.
    }

    ngOnDestroy(): void {
        this.projectChanges?.unsubscribe();
        this.annualReportProvider.resetActiveAnnualReport(); // Reset the active annual report
    }

    ngOnInit(): void {
        this.loadProject(this.findProjectIdBelow(this.activatedRoute.snapshot));

        // ResolveEnd comes before the router activates the pages of the new route. Together with
        // ProjectRouteReuseStrategy — which creates a project's pages anew when the project changes — no page
        // of the new project ever starts with the report of the project it replaces.
        this.projectChanges = this.router.events
            .pipe(filter((event): event is ResolveEnd => event instanceof ResolveEnd))
            .subscribe((event) => this.loadProject(this.findProjectIdBelow(event.state.root)));
    }

    /**
     * REV-22579: the active report id is restored from localStorage on every page load, so it can belong to
     * another project — the one last open in this or another tab. The report pages load and save with
     * whatever id they find first, so it is cleared before the project in the URL is loaded, and is set
     * again only from that project's data.
     */
    private loadProject(projectId: string | null | undefined): void {
        if (projectId === undefined || projectId === this.loadedProjectId) {
            return;
        }
        this.loadedProjectId = projectId;
        if (projectId == null) {
            return;
        }
        this.annualReportProvider.removeActiveAnnualReport();
        this.store.dispatch(ProjectsOverviewActions.fetchProjectDataByProjectId({ projectId }));
    }

    /**
     * The `:id` below this page's route in `snapshot`'s tree: `null` on a page without a project (the
     * overview), `undefined` when the tree leaves this page's routes.
     */
    private findProjectIdBelow(snapshot: ActivatedRouteSnapshot): string | null | undefined {
        let current: ActivatedRouteSnapshot | null = snapshot;
        while (current != null && current.routeConfig !== this.activatedRoute.routeConfig) {
            current = current.firstChild;
        }
        if (current == null) {
            return undefined;
        }
        for (; current != null; current = current.firstChild) {
            if (current.paramMap.has('id')) {
                return current.paramMap.get('id');
            }
        }
        return null;
    }
}
